// Considered: StatCardRowMolecule — rejected because it is a static row of plain StatCardAtom props; the section needs the
// capped list with "Show all", section-level loading and failed states and the store subscription. See spec Reuse Audit.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../../../context/ApiClientsContext';
import ButtonAtom from '../../../../atoms/Button/ButtonAtom';
import MessageAtom from '../../../../atoms/Message/MessageAtom';
import SkeletonAtom from '../../../../atoms/Skeleton/SkeletonAtom';
import IndicatorCardMolecule from '../../../../molecules/IndicatorCard/IndicatorCardMolecule';
import type { SphereSectionSlotContext } from '../../sphereSectionSlots';
import { composedMetricProperties } from './composedMetricProperties';
import { sphereIndicatorsStore } from './sphereIndicatorsStore';
import { useSphereIndicators } from './useSphereIndicators';
import './MetricIndicatorsSlotMolecule.scss';

const CARD_CAP = 6;
const KEY = 'sphere.indicators.section';

const MetricIndicatorsSlotMolecule: React.FC<SphereSectionSlotContext> = (ctx) => {
  const { t } = useTranslation();
  const sphereId = ctx.sphere.id;
  const state = useSphereIndicators(sphereId);
  const graphClient = useApiClientsOrNull()?.graphClient;
  const [showAll, setShowAll] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const retried = useRef(false);
  /** "Try again" presses on the reload notice that settled failed since it appeared; each one re-announces it. */
  const [failedRetries, setFailedRetries] = useState(0);

  // After "Try again" succeeds its button unmounts; focus then moves to the first card's Details button. A retry that
  // fails again leaves its button, and focus, where they are, and counts so the notice says the retry ran.
  useEffect(() => {
    if (state.status !== 'loaded') return;
    if (!state.refreshFailed) setFailedRetries(0);
    if (!retried.current) return;
    retried.current = false;
    if (state.refreshFailed) {
      setFailedRetries((n) => n + 1);
      return;
    }
    const active = document.activeElement;
    if (!active || active === document.body) {
      gridRef.current?.querySelector<HTMLElement>('button')?.focus();
    }
  }, [state]);

  const reload = sphereId && graphClient ? () => sphereIndicatorsStore.load({ sphereId, graphClient, force: true }) : undefined;

  const retry = () => {
    if (!reload) return;
    retried.current = true;
    reload();
  };

  if (state.status === 'failed' || (state.status === 'loading' && state.failedReads !== undefined)) {
    // The store keeps a failed first read through its retry, so the view, its button and focus stay put while it runs.
    // A retry that fails again replaces the message (new key, new attempt number), so it is announced again.
    const failedReads = state.failedReads ?? 1;
    return (
      <output className="metric-indicators-slot" data-testid="metric-indicators-failed">
        <MessageAtom
          key={failedReads}
          severity="error"
          text={failedReads <= 1 ? t(`${KEY}.loadFailed`) : t(`${KEY}.loadFailedAgain`, { count: failedReads - 1 })}
        />
        <ButtonAtom
          type="button"
          variant="text"
          size="small"
          label={t(`${KEY}.tryAgain`)}
          loading={state.status === 'loading'}
          onClick={retry}
          data-testid="metric-indicators-load-retry"
        />
      </output>
    );
  }

  if (state.status === 'idle' || (state.status === 'loading' && !state.indicators)) {
    const count = Math.min(Math.max(composedMetricProperties(ctx).length, 1), 3);
    return (
      <div className="metric-indicators-slot" data-testid="metric-indicators-loading" aria-busy="true">
        <div className="metric-indicators-slot__grid">
          {Array.from({ length: count }, (_, i) => (
            <SkeletonAtom key={i} height="6rem" />
          ))}
        </div>
      </div>
    );
  }

  const indicators = (state.indicators ?? []).filter((i) => i.access !== 'restricted');
  if (indicators.length === 0) return null;

  const capped = indicators.length > CARD_CAP;
  const visible = capped && !showAll ? indicators.slice(0, CARD_CAP) : indicators;
  const refreshFailed = state.refreshFailed === true;
  const reading = state.status === 'loading';

  return (
    <div className="metric-indicators-slot" data-testid="metric-indicators-cards">
      {/* Mounted with the cards so a failed re-read is announced when its message appears. The message is a plain
          container inside it: the region alone announces, politely. A retry that fails again replaces the message
          (new key, new attempt number), so the region announces that too. */}
      <output className="metric-indicators-slot__notice" data-testid="metric-indicators-reload-status">
        {refreshFailed ? (
          <MessageAtom
            key={failedRetries}
            severity="warn"
            inLiveRegion
            text={failedRetries === 0 ? t(`${KEY}.reloadFailed`) : t(`${KEY}.reloadFailedAgain`, { count: failedRetries })}
          />
        ) : null}
      </output>
      {refreshFailed ? (
        <ButtonAtom
          type="button"
          variant="text"
          size="small"
          label={t(`${KEY}.tryAgain`)}
          loading={reading}
          onClick={retry}
          data-testid="metric-indicators-reload-retry"
        />
      ) : null}
      <div ref={gridRef} className="metric-indicators-slot__grid">
        {visible.map((indicator) => (
          <IndicatorCardMolecule
            key={`${indicator.aspectSystemName}:${indicator.attachmentId ?? 'composed'}`}
            indicator={indicator}
            onRetry={reload}
            retryPending={reading}
          />
        ))}
      </div>
      {capped ? (
        <ButtonAtom
          type="button"
          variant="text"
          size="small"
          aria-expanded={showAll}
          label={showAll ? t(`${KEY}.showFewer`) : t(`${KEY}.showAll`, { count: indicators.length })}
          onClick={() => setShowAll((v) => !v)}
        />
      ) : null}
    </div>
  );
};

export default MetricIndicatorsSlotMolecule;
